import { HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';

import { Subscription } from 'rxjs';
import { BlobUploadRegistry, BlobUploadResponse } from 'src/app/core/model';
import { DataService } from 'src/app/shared/common-services/data.service';
import { DialogService } from 'src/app/shared/common-services/dialog.service';
import { SharedService } from 'src/app/shared/common-services/shared.service';

@Component({
  selector: 'app-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FileComponent
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: FileComponent
    }
  ]
})
export class FileComponent implements OnInit, OnDestroy, ControlValueAccessor {

  startUpload = false;
  isFileTypeSupport = true;
  fileName: string;
  fileType: string;
  fileUrl: any;
  fileSize: number;
  fileTypeImage: string = null;
  uploadedFileUrl: any = null;
  uploadProgress: number;

  private _touched = false;
  private _blobDetails: BlobUploadRegistry = {
    baseUrl: null,
    container: null,
    file: null
  };
  private _validationStatus: ValidationErrors | null;
  private _existDeleteStageCount = 0;
  private _fileDuration: string = null;
  private _file: File = null;
  private _blobSasToken: string = null;
  private _blobUploadSubscription: Subscription;
  private _blobDeleteSubscription: Subscription;

  @Input() folder: string;

  @Input('startUpload')
  set _startUpload(value: boolean) {
    if (value) {
      this.startUpload = value;
      this.uploadedFileUrl ? this._delete() : this._generateTokenAndUpload();
    }
  }

  @Input() pageType: string;

  @Input() image: string;

  @Input() isFileTypeImage: boolean;

  @Input()
  set content(value: Partial<BlobUploadResponse>) {
    this.fileName = value?.fileName;
    this.fileUrl = value?.fileUrl;
    this.fileSize = value?.fileSize;
    this.uploadedFileUrl = value?.fileUrl;
    this._setFileTypeImage(value?.fileName);
  }

  @Input() requiredFileSize: number = 1048576; // File size in bytes. Default file size 1 MB

  @Input() requiredFileType: string[];

  @Output() uploadResponse = new EventEmitter<Partial<BlobUploadResponse>>();

  @Input() fileSizeLimit: { errorMessage: string, fileSize: number };

  @Input() headerValid: boolean = false;

  constructor(
    public domSanitizer: DomSanitizer, private _service: SharedService, private _dialogService: DialogService, private _dataService: DataService
  ) { }

  @HostListener('change', ['$event.target.files']) emitFiles(event: FileList) {
    const file = event && event?.item(0);
    this._file = file;
    this.uploadedFileUrl = null;
    this.onChange(this._file);
    this.markAsTouched();
    this._upload();
  }

  ngOnInit(): void {
    this._blobRegistry();
  }

  onChange = (file) => { };
  onTouched = () => { };

  writeValue(file: File = null) {
    this._file = file;
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this._touched) {
      this.onTouched();
      this._touched = true;
    }
  }

  onUpload() {
    this._dataService.broadcast(this.pageType);
  }

  /**
   * Selected file delete
   */
  onSoftDelete() {
    // Updating the flag for deletion of existing image
    if (this.uploadedFileUrl) {
      this._existDeleteStageCount++;
    }
    // Other settings
    this._file = null;
    this.fileUrl = null;
    this.fileName = null;
    this.fileSize = null;
    this.fileTypeImage = null;
    this.isFileTypeSupport = true;
    this.onChange(null);
    this.uploadResponse.emit({
      stage: (this.uploadedFileUrl && this._existDeleteStageCount === 1) ? 'exist-delete' : 'soft-delete',
      status: true,
      fileDuration: null
    });
  }

  /**
   * Validates file 
   * @param control 
   * @returns validate 
   */
  validate(control: AbstractControl): ValidationErrors | null {
    this._validationStatus = null;
    const content: File = control?.value;
    // File type validation
    if (content?.type && !this.requiredFileType.includes(content?.type)) {
      this._validationStatus = {
        requiredFileType: true
      };
      return this._validationStatus;
    }
    // File size validation
    if (content?.size && content?.size > this.requiredFileSize) {
      this._validationStatus = {
        requiredFileSize: true
      };
      return this._validationStatus;
    }
    return this._validationStatus;
  }

  ngOnDestroy(): void {
    if (this._blobUploadSubscription) {
      this._blobUploadSubscription.unsubscribe();
    }
    if (this._blobDeleteSubscription) {
      this._blobDeleteSubscription.unsubscribe();
    }
  }

  /**
   * Sets file detials
   */
  private _setFileDetials() {
    this._blobDetails.file = this._file;
    this.fileName = this._file?.name;
    this.fileSize = this._file?.size;
    this.fileType = this._file?.type;
    this.isFileTypeSupport = this._service.getFileSupportStatus(this._file?.type, this.requiredFileType);
    this._setFileTypeImage(this._file?.name);
    this.uploadResponse.emit({
      stage: 'select',
      fileName: this.fileName,
      fileSize: this.fileSize,
      fileType: this.fileType,
      status: true,
      fileDuration: this._fileDuration,
      file: this._file
    });
  }


  /**
   * Durations time transform
   * @param value 
   * @returns  
   */
  private _durationTimeTransform(value: any) {
    const hours: number = Math.floor(value / 3600);
    const minutes: number = Math.floor(value / 60);
    const seconds: number = Math.floor(value - minutes * 60);
    return hours + ':' + minutes + ':' + seconds;
  }

  /**
   * Sets file type image
   * @param fileName 
   */
  private _setFileTypeImage(fileName: string) {
    this.fileTypeImage = this._service.getFileTypeImage(fileName);
  }

  /**
   * Uploads file
   */
  private _upload() {
    let reader = new FileReader();
    reader.readAsDataURL(this._file);
    reader.onload = () => {
      this.fileUrl = reader.result;
    };
    if (this._service.VIDEO_AUDIO_SUPPORTED_TYPE.includes(this._file?.type)) {
      // video duration finder
      const video = document.createElement('video');
      video.src = window.URL.createObjectURL(this._file);
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        this._fileDuration = this._durationTimeTransform(video.duration);
        this._setFileDetials();
        // video duration finder end
      };
    } else {
      this._fileDuration = null;
      this._setFileDetials();
    }
  }

  /**
   * Blobs registry
   */
  private _blobRegistry() {
    this._blobDetails.baseUrl = 'echo/documents';
  }

  /**
   * Generates token and upload
   */
  private _generateTokenAndUpload() {
    if (this._file) {
      this._service.generateSasToken(this._blobDetails).subscribe({
        next: (response: HttpResponse<any>) => {
          if (response?.body?.length) {
            this._blobSasToken = response?.body;
            this._uploadToBlob();
          }
          else
            this._blobSasToken = null;
        },
        error: () => {
          this._blobSasToken = null;
        }
      });
    } else {
      this.fileUrl = null;
      this.fileSize = null;
      this.fileName = null;
      this.uploadResponse.emit({
        fileUrl: this.fileUrl,
        fileSize: this.fileSize,
        fileName: this.fileName,
        fileType: this.fileType,
        fileDuration: null,
        stage: 'upload',
        status: false
      });
    }
  }

  /**
   * Uploads to blob
   */
  private _uploadToBlob() {
    this._blobUploadSubscription = this._service.blobUpload(this._blobDetails, this._blobSasToken).subscribe((response: HttpEvent<any>) => {
      if (response?.type == HttpEventType.UploadProgress) {
        this.uploadProgress = Math.round(100 * (response?.loaded / response?.total));
      } else if (response?.type == HttpEventType.Response) {
        this.uploadProgress = null;
        if (response?.status === 201) {
          this.fileUrl = response?.url;
          this._dialogService.showDialog('Info', 'Csv Uploaded Succesfully');
          this.uploadResponse.emit({
            fileUrl: this.fileUrl,
            fileSize: this.fileSize,
            fileName: this.fileName,
            fileType: this.fileType,
            fileDuration: this._fileDuration,
            stage: 'upload',
            status: true
          });
        } else {
          this.fileUrl = null;
          this.fileSize = null;
          this.fileName = null;
          this._dialogService.showDialog('Error', 'Unable to upload csv');
          this._service.toastrHandler({ type: 'error', msg: 'File upload failed' });
          this.uploadResponse.emit({
            fileUrl: this.fileUrl,
            fileSize: this.fileSize,
            fileName: this.fileName,
            fileType: this.fileType,
            fileDuration: null,
            stage: 'upload',
            status: false
          });
        }
      }
      this._blobSasToken = null;
    });
  }

  /**
   * Deletes uploaded file
   */
  private _delete() {
    if (this.uploadedFileUrl) {
      this._blobDeleteSubscription = this._service.blobDelete(this.uploadedFileUrl).subscribe((resp: any) => {
        if (resp.status === 202) {
          this.uploadedFileUrl = null;
        } else {
          this._service.toastrHandler({ type: 'error', msg: 'File delete failed' });
        }
        // Calling the upload file method only after the deletion of existing file
        this._generateTokenAndUpload();
      });
    }
  }
}
